/**
 * Undo/Redo hook for Todo Lists.
 *
 * Takes snapshots of the full list detail before each mutation.
 * On undo/redo, restores the snapshot to the React Query cache
 * and fires reverse API calls to sync the server.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import api from '@/lib/api';
import type { TodoListDetailResponse, TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useUndoRedo } from '@/lib/hooks/useUndoRedo';

interface UseTodoUndoRedoOptions {
  listId: string;
  enabled?: boolean;
}

interface UseTodoUndoRedoReturn {
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
  captureSnapshot: () => void;
  clear: () => void;
}

/**
 * Sync a target snapshot to the server by diffing against the current state.
 * Fires the minimal set of API calls to bring the server in line with the target.
 */
async function syncToServer(
  listId: string,
  current: TodoListDetailResponse,
  target: TodoListDetailResponse,
): Promise<void> {
  const ops: Promise<unknown>[] = [];

  // --- Items ---
  const currentItemMap = new Map(current.items.map((i) => [i.itemId, i]));
  const targetItemMap = new Map(target.items.map((i) => [i.itemId, i]));

  // Items to delete (in current but not target)
  for (const itemId of currentItemMap.keys()) {
    if (!targetItemMap.has(itemId)) {
      ops.push(api.deleteTodoItem(listId, itemId));
    }
  }

  // Items to create (in target but not current)
  for (const [itemId, targetItem] of targetItemMap) {
    if (!currentItemMap.has(itemId)) {
      ops.push(
        api.createTodoItem(listId, {
          title: targetItem.title,
          description: targetItem.description ?? undefined,
          sectionId: targetItem.sectionId ?? undefined,
        }),
      );
    }
  }

  // Items to update (in both but different)
  for (const [itemId, targetItem] of targetItemMap) {
    const currentItem = currentItemMap.get(itemId);
    if (!currentItem) continue;
    const diff = diffItem(currentItem, targetItem);
    if (diff) {
      ops.push(api.updateTodoItem(listId, itemId, diff));
    }
  }

  // --- Sections ---
  const currentSectionMap = new Map(current.sections.map((s) => [s.sectionId, s]));
  const targetSectionMap = new Map(target.sections.map((s) => [s.sectionId, s]));

  // Sections to delete (in current but not target)
  for (const sectionId of currentSectionMap.keys()) {
    if (!targetSectionMap.has(sectionId)) {
      ops.push(api.deleteTodoSection(listId, sectionId));
    }
  }

  // Sections to create (in target but not current)
  for (const [, targetSection] of targetSectionMap) {
    if (!currentSectionMap.has(targetSection.sectionId)) {
      ops.push(
        api.createTodoSection(listId, {
          name: targetSection.name,
          description: targetSection.description ?? undefined,
          parentSectionId: targetSection.parentSectionId ?? undefined,
          sectionType: targetSection.sectionType,
        }),
      );
    }
  }

  // Sections to update (in both but different)
  for (const [sectionId, targetSection] of targetSectionMap) {
    const currentSection = currentSectionMap.get(sectionId);
    if (!currentSection) continue;
    const diff = diffSection(currentSection, targetSection);
    if (diff) {
      ops.push(api.updateTodoSection(listId, sectionId, diff));
    }
  }

  // Execute all operations in parallel, swallow individual errors
  await Promise.allSettled(ops);

  // --- Reorder items per section ---
  const targetSectionIds = new Set<string | null>();
  for (const item of target.items) {
    targetSectionIds.add(item.sectionId);
  }
  for (const sectionId of targetSectionIds) {
    const targetOrder = target.items
      .filter((i) => i.sectionId === sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((i) => i.itemId);
    const currentOrder = current.items
      .filter((i) => i.sectionId === sectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((i) => i.itemId);
    if (JSON.stringify(targetOrder) !== JSON.stringify(currentOrder) && targetOrder.length > 0) {
      await api.reorderTodoItems(listId, sectionId, targetOrder).catch(() => {});
    }
  }

  // --- Reorder sections ---
  const targetSectionOrder = target.sections
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => s.sectionId);
  const currentSectionOrder = current.sections
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((s) => s.sectionId);
  if (
    JSON.stringify(targetSectionOrder) !== JSON.stringify(currentSectionOrder) &&
    targetSectionOrder.length > 0
  ) {
    await api.reorderTodoSections(listId, targetSectionOrder).catch(() => {});
  }
}

function diffItem(
  current: TodoItemResponse,
  target: TodoItemResponse,
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};
  if (current.title !== target.title) changes.title = target.title;
  if (current.description !== target.description) changes.description = target.description;
  if (current.status !== target.status) changes.status = target.status;
  if (current.sectionId !== target.sectionId) changes.sectionId = target.sectionId;
  return Object.keys(changes).length > 0 ? changes : null;
}

function diffSection(
  current: TodoSectionResponse,
  target: TodoSectionResponse,
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};
  if (current.name !== target.name) changes.name = target.name;
  if (current.description !== target.description) changes.description = target.description;
  if (current.sectionType !== target.sectionType) changes.sectionType = target.sectionType;
  if (current.parentSectionId !== target.parentSectionId)
    changes.parentSectionId = target.parentSectionId;
  return Object.keys(changes).length > 0 ? changes : null;
}

export function useTodoUndoRedo({
  listId,
  enabled = true,
}: UseTodoUndoRedoOptions): UseTodoUndoRedoReturn {
  const queryClient = useQueryClient();
  const detailKey = queryKeys.todoLists.detail(listId);

  const {
    canUndo,
    canRedo,
    snapshot,
    undo: undoRaw,
    redo: redoRaw,
    clear,
  } = useUndoRedo<TodoListDetailResponse>({
    maxHistory: 50,
  });

  // Track whether a mutation is pending so we can snapshot the result
  const pendingMutation = useRef(false);
  // Suppress cache subscription during undo/redo apply
  const isApplying = useRef(false);

  // Capture the initial state on first load so undo has a baseline to go back to
  const initialCaptured = useRef(false);

  // Subscribe to query cache changes to capture post-mutation states
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (isApplying.current) return;
      if (event.type === 'updated' && event.action.type === 'success') {
        const queryKey = event.query.queryKey;
        if (JSON.stringify(queryKey) === JSON.stringify(detailKey)) {
          const data = event.query.state.data as TodoListDetailResponse | undefined;
          if (!data) return;

          if (!initialCaptured.current) {
            snapshot(data);
            initialCaptured.current = true;
            return;
          }

          if (pendingMutation.current) {
            pendingMutation.current = false;
            snapshot(data);
          }
        }
      }
    });
    return unsubscribe;
  }, [enabled, queryClient, detailKey, snapshot]);

  const captureSnapshot = useCallback(() => {
    if (!enabled) return;
    // Capture the "before" state
    const current = queryClient.getQueryData<TodoListDetailResponse>(detailKey);
    if (current) {
      snapshot(current);
    }
    // Mark that the "after" state should be captured when the cache updates
    pendingMutation.current = true;
  }, [enabled, queryClient, detailKey, snapshot]);

  const applyState = useCallback(
    async (targetState: TodoListDetailResponse | null) => {
      if (!targetState) return;

      // Get the current server-synced state before we overwrite the cache
      const currentState = queryClient.getQueryData<TodoListDetailResponse>(detailKey);

      // Suppress cache subscription during apply
      isApplying.current = true;

      // Cancel in-flight queries
      await queryClient.cancelQueries({ queryKey: detailKey });

      // Immediately restore the UI
      queryClient.setQueryData<TodoListDetailResponse>(detailKey, targetState);

      // Re-enable cache subscription
      isApplying.current = false;

      // Sync the server in the background
      if (currentState) {
        syncToServer(listId, currentState, targetState).finally(() => {
          queryClient.invalidateQueries({ queryKey: detailKey });
        });
      }
    },
    [queryClient, detailKey, listId],
  );

  const undo = useCallback(() => {
    if (!enabled) return;
    const state = undoRaw();
    applyState(state);
  }, [enabled, undoRaw, applyState]);

  const redo = useCallback(() => {
    if (!enabled) return;
    const state = redoRaw();
    applyState(state);
  }, [enabled, redoRaw, applyState]);

  const clearAll = useCallback(() => {
    clear();
    initialCaptured.current = false;
    pendingMutation.current = false;
  }, [clear]);

  return {
    canUndo: enabled && canUndo,
    canRedo: enabled && canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearAll,
  };
}
